import { getErrorMessage } from '@/lib/functions';
import i18n from '@/i18n';
﻿import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { useAuth } from '@/contexts/AuthContext';
import type { Profile, ProfileUpdate } from '@/lib/database.types';
import { getMyProfile, updateProfile as updateProfileApi } from '@/lib/profiles';

type ProfileContextValue = {
  profile: Profile | null;
  /** true όσο φορτώνεται το προφίλ του τρέχοντος χρήστη. */
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  save: (patch: ProfileUpdate) => Promise<Profile>;
};

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

export function ProfileProvider({ children }: PropsWithChildren) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [profile, setProfile] = useState<Profile | null>(null);
  // Ξεκινά true ώστε οι guards να μην τρέχουν redirect πριν φορτωθεί το προφίλ.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setProfile(await getMyProfile());
    } catch (e) {
      setError(getErrorMessage(e, i18n.t('common.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Φόρτωσε ξανά κάθε φορά που αλλάζει ο συνδεδεμένος χρήστης.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (patch: ProfileUpdate) => {
      if (!userId) throw new Error('Δεν υπάρχει συνδεδεμένος χρήστης.');
      const updated = await updateProfileApi(userId, patch);
      setProfile(updated);
      return updated;
    },
    [userId],
  );

  const value = useMemo<ProfileContextValue>(
    () => ({ profile, loading, error, refresh, save }),
    [profile, loading, error, refresh, save],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error('Το useProfile πρέπει να χρησιμοποιείται μέσα σε <ProfileProvider>.');
  }
  return ctx;
}
